import { displayItemLink } from './itemPresentation.js'

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatEuro(value) {
  const amount = Number.parseFloat(value || 0)
  const safe = Number.isFinite(amount) ? amount : 0
  return `${safe.toFixed(2).replace('.', ',')} €`
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('fr-FR')
}

function safeHref(link) {
  try {
    const url = new URL(String(link || '').trim())
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.href
  } catch {
    return ''
  }
  return ''
}

export function exportProductLinkLabel(link) {
  const href = safeHref(link)
  if (!href) return ''
  return displayItemLink(href) || 'Voir le produit'
}

function renderProductLink(link) {
  const href = safeHref(link)
  if (!href) return '<span class="product-link-empty">Sans lien</span>'
  const label = escapeHtml(exportProductLinkLabel(link) || 'Voir le produit')
  return `<a class="product-link" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`
}

function imageFor(item, index, imageMap) {
  const dataUrl = imageMap instanceof Map ? imageMap.get(index) : imageMap?.[index]
  if (!dataUrl) return '<div class="product-image is-empty" aria-hidden="true">Sans image</div>'
  const alt = escapeHtml(item?.name || 'Produit')
  return `<img class="product-image" src="${escapeHtml(dataUrl)}" alt="${alt}">`
}

export function buildExportedCartHtml({ cart, items = [], imageMap } = {}) {
  const name = cart?.name || 'Sans nom'
  const created = formatDate(cart?.created_at)
  const updated = formatDate(cart?.updated_at)
  const rows = Array.isArray(items) ? items : []
  const total = rows.reduce((sum, item) => {
    const price = Number.parseFloat(item?.price || 0)
    const quantity = Number.parseInt(item?.quantity || 1, 10)
    const safePrice = Number.isFinite(price) ? price : 0
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    return sum + safePrice * safeQty
  }, 0)

  const productCards = rows.map((item, index) => {
    const price = Number.parseFloat(item?.price || 0)
    const quantity = Number.parseInt(item?.quantity || 1, 10)
    const safePrice = Number.isFinite(price) ? price : 0
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    const lineTotal = safePrice * safeQty
    const promo = String(item?.promo || '').trim()
    return `
      <article class="product">
        <div class="product-media">${imageFor(item, index, imageMap)}</div>
        <div class="product-body">
          <h2 class="product-name">${escapeHtml(item?.name || 'Sans nom')}</h2>
          <p class="product-qty">Quantité <strong>${escapeHtml(safeQty)}</strong></p>
          ${promo ? `<p class="product-promo">${escapeHtml(promo)}</p>` : ''}
          ${renderProductLink(item?.link)}
        </div>
        <div class="product-amounts">
          <p class="amount"><span>Prix unitaire</span><strong>${escapeHtml(formatEuro(safePrice))}</strong></p>
          <p class="amount is-total"><span>Total ligne</span><strong>${escapeHtml(formatEuro(lineTotal))}</strong></p>
        </div>
      </article>`
  }).join('')

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(name)} — Cam’it</title>
  <style>
    :root {
      --page: #d4d5d1;
      --surface: #e6e6e2;
      --ink: #1c1d22;
      --muted: #4f5158;
      --line: rgba(28, 29, 34, 0.14);
      --soft: rgba(28, 29, 34, 0.07);
    }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      min-height: 100vh;
      font-family: "Segoe UI", "Helvetica Neue", sans-serif;
      background: var(--page);
      color: var(--ink);
      line-height: 1.45;
    }
    .page {
      width: min(920px, calc(100% - 32px));
      margin: 24px auto 40px;
    }
    .header, .product, .summary {
      background: var(--surface);
      border: 1px solid var(--line);
      border-radius: 18px;
    }
    .header {
      padding: 22px 24px;
      margin-bottom: 16px;
    }
    .header h1 {
      margin: 0 0 8px;
      font-size: 1.55rem;
      font-weight: 700;
      letter-spacing: -0.02em;
    }
    .header-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      margin: 0;
      padding: 0;
      list-style: none;
      color: var(--muted);
      font-size: 0.92rem;
    }
    .products { display: grid; gap: 12px; }
    .product {
      display: grid;
      grid-template-columns: 132px minmax(0, 1fr) auto;
      gap: 16px;
      align-items: center;
      padding: 14px;
    }
    .product-media {
      width: 132px;
      height: 132px;
      display: grid;
      place-items: center;
      background: var(--soft);
      border-radius: 14px;
      overflow: hidden;
    }
    .product-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }
    .product-image.is-empty {
      color: var(--muted);
      font-size: 0.8rem;
    }
    .product-name {
      margin: 0 0 8px;
      font-size: 1.05rem;
      font-weight: 650;
    }
    .product-qty, .product-promo {
      margin: 0 0 6px;
      color: var(--muted);
      font-size: 0.92rem;
    }
    .product-link {
      display: inline-block;
      max-width: 100%;
      color: var(--ink);
      font-size: 0.9rem;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
      overflow-wrap: anywhere;
    }
    .product-link-empty { color: var(--muted); font-size: 0.9rem; }
    .product-amounts {
      display: grid;
      gap: 8px;
      min-width: 9.5rem;
      text-align: right;
    }
    .amount { margin: 0; }
    .amount span {
      display: block;
      color: var(--muted);
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .amount strong {
      display: block;
      font-size: 1rem;
      font-variant-numeric: tabular-nums;
    }
    .summary {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      margin-top: 16px;
      padding: 18px 22px;
    }
    .summary p { margin: 0; color: var(--muted); }
    .summary strong {
      display: block;
      margin-top: 4px;
      color: var(--ink);
      font-size: 1.35rem;
    }
    @media (max-width: 720px) {
      .page { width: min(100% - 20px, 920px); margin: 12px auto 28px; }
      .product {
        grid-template-columns: 112px minmax(0, 1fr);
        align-items: start;
      }
      .product-media, .product-image, .product-image.is-empty {
        width: 112px;
        height: 112px;
      }
      .product-amounts {
        grid-column: 1 / -1;
        grid-template-columns: 1fr 1fr;
        text-align: left;
        min-width: 0;
        padding-top: 4px;
        border-top: 1px solid var(--line);
      }
      .summary { flex-direction: column; }
    }
  </style>
</head>
<body>
  <main class="page">
    <header class="header">
      <h1>${escapeHtml(name)}</h1>
      <ul class="header-meta">
        <li>${escapeHtml(rows.length)} produit${rows.length > 1 ? 's' : ''}</li>
        ${created ? `<li>Créé le ${escapeHtml(created)}</li>` : ''}
        ${updated ? `<li>Mis à jour le ${escapeHtml(updated)}</li>` : ''}
      </ul>
    </header>
    <section class="products">${productCards}</section>
    <footer class="summary">
      <p>Produits<strong>${escapeHtml(rows.length)}</strong></p>
      <p>Total du panier<strong>${escapeHtml(formatEuro(total))}</strong></p>
    </footer>
  </main>
</body>
</html>`
}
